import React from 'react';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import { IndustrySolution, industrySolutions } from 'components/pages/IndustrySolutions/data';
import { audienceSolutions } from 'components/pages/AudienceSolutions/data';
import { comparisonHub, comparisonPages, getComparisonByPath } from 'components/pages/Compare/data';
import {
  FIGMA_TO_STORYCARDS_DESCRIPTION,
  FIGMA_TO_STORYCARDS_SCHEMA,
  FIGMA_TO_STORYCARDS_TITLE,
} from 'components/pages/FigmaToStorycards/data';

type MetaContentProps = {
  title: string;
  description: string;
  url: string;
  robots?: string;
  pageTitle?: string;
  image?: string;
  imageAlt?: string;
  ogType?: 'website' | 'profile';
  structuredData?: object;
};

function MetaContent({
  title,
  description,
  url,
  robots,
  pageTitle,
  image = 'https://storycards.com/share.jpg',
  imageAlt = 'Storycards',
  ogType = 'website',
  structuredData,
}: MetaContentProps) {
  const resolvedTitle = pageTitle || title;
  const isIndexable = !robots?.includes('noindex');
  const pageIdUrl = url === 'https://storycards.com' ? `${url}/` : url;
  const resolvedStructuredData =
    structuredData ||
    (isIndexable
      ? {
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          '@id': `${pageIdUrl}#webpage`,
          url,
          name: resolvedTitle,
          description,
          inLanguage: 'en',
          isPartOf: { '@id': 'https://storycards.com/#website' },
        }
      : undefined);

  return (
    <Helmet>
      <title>{resolvedTitle}</title>
      <meta name="description" content={description} />
      <meta property="og:title" content={resolvedTitle} />
      <meta content={description} property="og:description" />
      <meta content={resolvedTitle} name="twitter:title" />
      <meta content={description} name="twitter:description" />
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content="Storycards" />
      <meta property="og:locale" content="en_US" />
      <meta content="summary_large_image" name="twitter:card" />
      <meta name="twitter:site" content="@_Storycards" />
      <meta property="og:image" content={image} />
      <meta property="og:image:alt" content={imageAlt} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={imageAlt} />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-title" content="Storycards" />
      <meta name="robots" content={robots ? robots : 'index, follow, max-image-preview:large'} />
      <link rel="canonical" href={url} />
      <meta property="og:url" content={url} />
      {resolvedStructuredData && <script type="application/ld+json">{JSON.stringify(resolvedStructuredData)}</script>}
    </Helmet>
  );
}

const ABOUT_DESCRIPTION =
  'Meet Storycards, the enterprise platform for creating, governing and continuously improving branded interactive experiences across teams, brands and markets.';

const HOSTING_DESCRIPTION =
  'Publish Storycards products on managed infrastructure with a global CDN, traffic surge scaling, SSL/TLS, hosting-layer protection and optimized media delivery.';

const HOSTING_PAGE_SCHEMA = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebPage',
      '@id': 'https://storycards.com/hosting#webpage',
      url: 'https://storycards.com/hosting',
      name: 'Enterprise Managed Hosting for Interactive Products | Storycards',
      description: HOSTING_DESCRIPTION,
      inLanguage: 'en',
      isPartOf: {
        '@type': 'WebSite',
        '@id': 'https://storycards.com/#website',
        url: 'https://storycards.com',
        name: 'Storycards',
      },
      about: { '@id': 'https://storycards.com/hosting#service' },
    },
    {
      '@type': 'Service',
      '@id': 'https://storycards.com/hosting#service',
      name: 'Storycards Managed Hosting',
      serviceType: 'Managed hosting for interactive products',
      description: HOSTING_DESCRIPTION,
      provider: { '@id': 'https://storycards.com/#organization' },
      areaServed: 'Worldwide',
    },
  ],
};

const ABOUT_PAGE_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  '@id': 'https://storycards.com/about#webpage',
  url: 'https://storycards.com/about',
  name: 'About Storycards',
  description: ABOUT_DESCRIPTION,
  inLanguage: 'en',
  isPartOf: {
    '@type': 'WebSite',
    '@id': 'https://storycards.com/#website',
    url: 'https://storycards.com',
    name: 'Storycards',
  },
  about: { '@id': 'https://storycards.com/#organization' },
  mainEntity: { '@id': 'https://storycards.com/#organization' },
  primaryImageOfPage: {
    '@type': 'ImageObject',
    url: 'https://storycards.com/storycards-16x9.jpg',
  },
};

const GIL_RABBI_DESCRIPTION =
  'Gil Rabbi founded Storycards and has more than 20 years of experience building interactive products and real-time audience participation technology.';

const GIL_RABBI_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'ProfilePage',
  '@id': 'https://storycards.com/gil-rabbi#webpage',
  url: 'https://storycards.com/gil-rabbi',
  name: 'Gil Rabbi | Founder of Storycards',
  description: GIL_RABBI_DESCRIPTION,
  inLanguage: 'en',
  mainEntity: {
    '@type': 'Person',
    '@id': 'https://storycards.com/gil-rabbi',
    name: 'Gil Rabbi',
    alternateName: 'גיל רבי',
    givenName: 'Gil',
    familyName: 'Rabbi',
    jobTitle: 'Founder',
    description: GIL_RABBI_DESCRIPTION,
    image: 'https://storycards.com/gilrabbi.jpg',
    url: 'https://storycards.com/gil-rabbi',
    worksFor: { '@id': 'https://storycards.com/#organization' },
    sameAs: [
      'https://www.linkedin.com/in/gilrabbi/',
      'https://www.crunchbase.com/person/gil-rabbi',
      'https://he.wikipedia.org/wiki/%D7%92%D7%99%D7%9C_%D7%A8%D7%91%D7%99',
      'https://twitter.com/GilRabbi',
      'https://www.instagram.com/gilrabbi',
    ],
  },
};

const getSolutionStructuredData = (solution: IndustrySolution) => {
  const url = `https://storycards.com${solution.route}`;

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${url}#webpage`,
        url,
        name: solution.seo.title,
        description: solution.seo.description,
        inLanguage: 'en',
        isPartOf: {
          '@type': 'WebSite',
          '@id': 'https://storycards.com/#website',
          url: 'https://storycards.com',
          name: 'Storycards',
        },
        about: { '@id': `${url}#service` },
      },
      {
        '@type': 'Service',
        '@id': `${url}#service`,
        name: `Storycards for ${solution.name}`,
        serviceType: 'Interactive customer engagement platform',
        description: solution.seo.description,
        provider: { '@id': 'https://storycards.com/#organization' },
        audience: {
          '@type': 'BusinessAudience',
          audienceType: solution.name,
        },
        areaServed: 'Worldwide',
      },
    ],
  };
};

const toFaqEntities = (faqs: Array<{ question: string; answer: string }>) =>
  faqs.map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item.answer,
    },
  }));

const getComparisonStructuredData = (comparison: typeof comparisonPages[number]) => {
  const url = `https://storycards.com${comparison.route}`;

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': ['WebPage', 'FAQPage'],
        '@id': `${url}#webpage`,
        url,
        name: comparison.seo.title,
        description: comparison.seo.description,
        inLanguage: 'en',
        dateModified: '2026-10-03',
        isPartOf: { '@id': 'https://storycards.com/#website' },
        about: [
          { '@type': 'Thing', name: 'Storycards' },
          { '@type': 'Thing', name: comparison.competitor },
        ],
        mainEntity: toFaqEntities(comparison.page.faqs || []),
        breadcrumb: { '@id': `${url}#breadcrumb` },
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${url}#breadcrumb`,
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://storycards.com/' },
          { '@type': 'ListItem', position: 2, name: 'Compare platforms', item: 'https://storycards.com/compare' },
          { '@type': 'ListItem', position: 3, name: `Storycards vs ${comparison.competitor}`, item: url },
        ],
      },
    ],
  };
};

const getComparisonHubStructuredData = () => {
  const url = 'https://storycards.com/compare';

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': ['CollectionPage', 'FAQPage'],
        '@id': `${url}#webpage`,
        url,
        name: comparisonHub.seo.title,
        description: comparisonHub.seo.description,
        inLanguage: 'en',
        dateModified: '2026-10-03',
        isPartOf: { '@id': 'https://storycards.com/#website' },
        mainEntity: toFaqEntities(comparisonHub.page.faqs || []),
        hasPart: { '@id': `${url}#comparison-list` },
        breadcrumb: { '@id': `${url}#breadcrumb` },
      },
      {
        '@type': 'ItemList',
        '@id': `${url}#comparison-list`,
        name: 'Storycards platform comparisons',
        numberOfItems: comparisonPages.length,
        itemListElement: comparisonPages.map((item, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: `Storycards vs ${item.competitor}`,
          url: `https://storycards.com${item.route}`,
        })),
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${url}#breadcrumb`,
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://storycards.com/' },
          { '@type': 'ListItem', position: 2, name: 'Compare platforms', item: url },
        ],
      },
    ],
  };
};

type MetaProps = {
  page?: string;
};

function Meta({ page }: MetaProps) {
  const pathname = useLocation().pathname.replace(/\/+$/, '') || '/';
  const location = pathname.slice(1);
  const solution = [...audienceSolutions, ...industrySolutions].find((item) => item.route === pathname);
  const comparison = getComparisonByPath(pathname);

  if (solution) {
    return (
      <MetaContent
        title={solution.seo.title}
        description={solution.seo.description}
        url={`https://storycards.com${solution.route}`}
        structuredData={getSolutionStructuredData(solution)}
      />
    );
  }

  if (comparison) {
    return (
      <MetaContent
        title={comparison.seo.title}
        description={comparison.seo.description}
        url={`https://storycards.com${comparison.route}`}
        imageAlt={`Storycards and ${comparison.competitor} platform comparison`}
        structuredData={getComparisonStructuredData(comparison)}
      />
    );
  }

  if (pathname === comparisonHub.route) {
    return (
      <MetaContent
        title={comparisonHub.seo.title}
        description={comparisonHub.seo.description}
        url="https://storycards.com/compare"
        imageAlt="Storycards platform comparison guide"
        structuredData={getComparisonHubStructuredData()}
      />
    );
  }

  switch (location) {
    case '':
      return (
        <MetaContent
          title="Storycards"
          pageTitle="Enterprise AI Builder & Data Collection Platform | Storycards"
          description="Build and scale on-brand interactive experiences on a governed platform. Turn interactions into first-party data that drives engagement and performance."
          url="https://storycards.com"
        />
      );
    case 'homepage-old':
      return (
        <MetaContent
          title="Storycards"
          description="Storycards is the most professional editor to build premium engagement and data collection products in a completely visual canvas, enhanced by AI and no-code technology."
          url="https://storycards.com"
          robots="noindex, nofollow"
        />
      );
    case 'flow':
      return (
        <MetaContent
          title="Visual Flow Builder for Interactive Journeys | Storycards"
          description="Build adaptive journeys with conditional paths, scoring, personalization and step-level performance data."
          url="https://storycards.com/flow"
        />
      );
    case 'studio':
      return (
        <MetaContent
          title="Professional No-Code Studio for Designers | Storycards"
          description="Design pixel-perfect, responsive interactive products with AI, Figma integration, reusable design systems, advanced motion and enterprise publishing control."
          url="https://storycards.com/studio"
        />
      );
    case 'figma-to-storycards':
      return (
        <MetaContent
          title={FIGMA_TO_STORYCARDS_TITLE}
          description={FIGMA_TO_STORYCARDS_DESCRIPTION}
          url="https://storycards.com/figma-to-storycards"
          imageAlt="Storycards"
          structuredData={FIGMA_TO_STORYCARDS_SCHEMA}
        />
      );
    case 'editor':
      return (
        <MetaContent
          title="Professional No-Code Studio for Designers | Storycards"
          description="Design pixel-perfect, responsive interactive products with AI, Figma integration, reusable design systems, advanced motion and enterprise publishing control."
          url="https://storycards.com/studio"
          robots="noindex, follow"
        />
      );
    case 'create-segments':
      return (
        <MetaContent
          title="First-Party Audience Segmentation & Activation | Storycards"
          description="Build audience segments from answers and actions in Storycards, then trigger marketing pixels or send structured data to CRM, analytics and internal systems."
          url="https://storycards.com/create-segments"
        />
      );
    case 'cards-format':
      return (
        <MetaContent
          title="Engagement & Flow Engine for Interactive Journeys | Storycards"
          description="Build measurable interactive journeys with flexible flow logic, structured first-party data, drop-off visibility and continuous optimization."
          url="https://storycards.com/cards-format"
        />
      );
    case 'collaboration':
      return (
        <MetaContent
          title="Enterprise Team Collaboration and Governance | Storycards"
          description="Organize teams, products and templates with group access, role-based permissions, publishing controls, revision history and organization-wide analytics."
          url="https://storycards.com/collaboration"
        />
      );
    case 'analytics':
      return (
        <MetaContent
          title="Journey Analytics for Interactive Products | Storycards"
          description="Measure views, choices, completion and drop-off in Storycards, then send automatic and custom events to Google Analytics and your existing data stack."
          url="https://storycards.com/analytics"
        />
      );
    case 'data-collection':
      return (
        <MetaContent
          title="First-Party Data Collection and Control | Storycards"
          description="Collect structured audience data, manage access and send records to the CRM, database or server your organization controls."
          url="https://storycards.com/data-collection"
        />
      );
    case 'invite':
      return (
        <MetaContent
          title="Join Storycards"
          description="Accept your invitation and join your team’s Storycards workspace."
          url="https://storycards.com/invite"
          robots="noindex, nofollow"
        />
      );
    case 'about':
      return (
        <MetaContent
          title="About Storycards | Enterprise Interactive Experience Platform"
          description={ABOUT_DESCRIPTION}
          url="https://storycards.com/about"
          image="https://storycards.com/storycards-16x9.jpg"
          imageAlt="Storycards enterprise interactive experience platform"
          structuredData={ABOUT_PAGE_SCHEMA}
        />
      );
    case 'press-center':
      return (
        <MetaContent
          title="Press Coverage and Media Contact | Storycards"
          description="Read published coverage of Storycards and Gil Rabbi, and find contact details for press inquiries."
          url="https://storycards.com/press-center"
        />
      );
    case 'privacy-policy':
      return (
        <MetaContent
          title="Privacy Policy | Storycards"
          description="Read how Storycards collects, uses, stores and protects personal information."
          url="https://storycards.com/privacy-policy"
        />
      );
    case 'dpa':
      return (
        <MetaContent
          title="Data Processing Agreement | Storycards"
          description="Review the Storycards terms for processing personal data on behalf of customers."
          url="https://storycards.com/dpa"
        />
      );
    case 'enterprise':
      return (
        <MetaContent
          title="Storycards for Enterprises & Brands"
          description="One governed platform for enterprise teams to create, manage and optimize on-brand interactive experiences while maintaining control of permissions, security and data."
          url="https://storycards.com/enterprise"
          robots="noindex, nofollow"
        />
      );
    case 'startups':
      return (
        <MetaContent
          title="Storycards for Enterprises & Brands"
          description="One governed platform for enterprise teams to create, manage and optimize on-brand interactive experiences while maintaining control of permissions, security and data."
          url="https://storycards.com/enterprise"
          robots="noindex, follow"
        />
      );
    case 'gil-rabbi':
      return (
        <MetaContent
          title="Gil Rabbi | Founder of Storycards"
          description={GIL_RABBI_DESCRIPTION}
          url="https://storycards.com/gil-rabbi"
          image="https://storycards.com/gilrabbi.jpg"
          imageAlt="Gil Rabbi, Founder of Storycards"
          ogType="profile"
          structuredData={GIL_RABBI_SCHEMA}
        />
      );
    case 'ai':
      return (
        <MetaContent
          title="Governed AI Creation for Enterprises | Storycards"
          description="Create governed interactive products with AI, capture structured first-party data, personalize journeys and improve performance with analytics and A/B testing."
          url="https://storycards.com/ai"
        />
      );
    case 'brand-governance':
      return (
        <MetaContent
          title="Enterprise Brand Governance | Storycards"
          description="Give designers control of templates, components and protected editing while enterprise teams create within approved brand and publishing rules."
          url="https://storycards.com/brand-governance"
        />
      );
    case 'integrations':
      return (
        <MetaContent
          title="Enterprise Integrations and Data Connections | Storycards"
          description="Connect every Storycards product to your enterprise stack with built-in integrations, webhooks and a custom API client for real-time data flows."
          url="https://storycards.com/integrations"
        />
      );
    case 'hosting':
      return (
        <MetaContent
          title="Enterprise Managed Hosting for Interactive Products | Storycards"
          description={HOSTING_DESCRIPTION}
          url="https://storycards.com/hosting"
          imageAlt="Storycards managed hosting for interactive products"
          structuredData={HOSTING_PAGE_SCHEMA}
        />
      );
    case 'engagement-ai':
      return (
        <MetaContent
          title="Engagement AI Engine for Interactive Products | Storycards"
          description="Turn URLs and prompts into branded engagement products, analyze real interaction data and improve performance with Storycards AI."
          url="https://storycards.com/engagement-ai"
        />
      );
    case 'security':
      return (
        <MetaContent
          title="Enterprise Security, Data Control and Access | Storycards"
          description="Review Storycards controls for SSO, governed AI, encryption, infrastructure protection, secure file processing, customer-controlled data flows and testing."
          url="https://storycards.com/security"
        />
      );
    case 'api':
      return (
        <MetaContent
          title="Real-Time Enterprise Data API | Storycards"
          description="Send Storycards clicks, answers, submissions and journey events to your CRM, analytics, databases and internal systems in real time."
          url="https://storycards.com/api"
        />
      );
    case 'not-found':
      return (
        <MetaContent
          title="Page Not Found | Storycards"
          description="The page you requested could not be found."
          url="https://storycards.com/not-found"
          robots="noindex, nofollow"
        />
      );
    default:
      return (
        <MetaContent
          title="Page Not Found | Storycards"
          description="The page you requested could not be found."
          url="https://storycards.com/not-found"
          robots="noindex, nofollow"
        />
      );
  }
}

export default Meta;
